import React, { useContext, useMemo, useState } from 'react';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { DataContext } from '../../components/DataContext';
import '../../styles/components/_gamesPerformanceTable.css';

const playerColumns = [
  { key: 'playerName', label: 'Player', type: 'text' },
  { key: 'gamesPlayed', label: 'Games' },
  { key: 'avgPlayersPlayedAgainst', label: 'Avg Players' },
  { key: 'totalBuyIn', label: 'Buy In' },
  { key: 'totalBuyBack', label: 'Buy Back' },
  { key: 'totalMoneyIn', label: 'Total In' },
  { key: 'totalMoneyOut', label: 'Total Out' },
  { key: 'winnings', label: 'Winnings' },
  { key: 'return', label: 'Return' },
];

const formatCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '...';
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};

const formatDecimal = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount.toFixed(2) : '...';
};

const formatReturn = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? `${amount.toFixed(2)}%` : '...';
};

export default function PlayersTable() {
  const { playersData } = useContext(DataContext);
  const [sortKey, setSortKey] = useState('playerName');
  const [sortDirection, setSortDirection] = useState('asc');

  const players = useMemo(
    () => playersData && typeof playersData === 'object' ? Object.values(playersData) : [],
    [playersData]
  );

  const sortedPlayers = useMemo(() => {
    const directionMultiplier = sortDirection === 'asc' ? 1 : -1;
    const column = playerColumns.find(({ key }) => key === sortKey);

    return [...players].sort((playerA, playerB) => {
      const valueA = column?.type === 'text' ? String(playerA[sortKey]).toLowerCase() : Number(playerA[sortKey]);
      const valueB = column?.type === 'text' ? String(playerB[sortKey]).toLowerCase() : Number(playerB[sortKey]);
      const comparison = valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
      return comparison * directionMultiplier;
    });
  }, [players, sortDirection, sortKey]);

  const handleSort = (nextSortKey) => {
    if (nextSortKey === sortKey) {
      setSortDirection((currentDirection) => currentDirection === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortKey(nextSortKey);
    setSortDirection('asc');
  };

  const renderValue = (player, key) => {
    if (key === 'playerName' || key === 'gamesPlayed') return player[key];
    if (key === 'avgPlayersPlayedAgainst') return formatDecimal(player[key]);
    if (key === 'return') return formatReturn(player[key]);
    return formatCurrency(player[key]);
  };

  return (
    <section className="games-performance players-performance" aria-label="Player statistics">
      <div className="games-performance-meta"><span>{players.length} Players</span></div>
      <div className="games-performance-scroll">
        <table className="games-performance-table players-performance-table">
          <thead>
            <tr>
              {playerColumns.map((column) => (
                <th aria-sort={column.key === sortKey ? `${sortDirection}ending` : 'none'} key={column.key}>
                  <button
                    className={`performance-sort-button${column.key === sortKey ? ' performance-sort-button-active' : ''}`}
                    type="button"
                    onClick={() => handleSort(column.key)}
                  >
                    <span>{column.label}</span>
                    {column.key === sortKey && (sortDirection === 'asc'
                      ? <ArrowUpward className="performance-sort-icon" aria-hidden="true" />
                      : <ArrowDownward className="performance-sort-icon" aria-hidden="true" />)}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedPlayers.map((player, index) => (
              <tr key={player.playerName} style={{ '--row-i': index }}>
                {playerColumns.map((column) => <td key={column.key}>{renderValue(player, column.key)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!players.length && <p className="games-performance-empty">Loading players...</p>}
    </section>
  );
}